/** Vendor pitched-roof cladding types (must match Django ``VANDERVALK_PITCHED_CLADDING_TYPES``). */
export const VANDERVALK_PITCHED_CLADDING_TYPES = [
  'STEEL_DECK',
  'CORRUGATED',
  'STANDING_SEAM',
  'TILED',
  'SLATE',
  'PLAIN',
  'MEMBRANE',
] as const

export type VanDerValkPitchedCladdingType = (typeof VANDERVALK_PITCHED_CLADDING_TYPES)[number]

export const VANDERVALK_STEEL_THICKNESS_OPTIONS = [0.35, 0.5, 0.55, 0.63, 0.75, 0.88, 1, 1.13, 1.25] as const

export const VANDERVALK_STANDING_SEAM_TYPE_OPTIONS = ['FLAT_SEAM', 'ROUND_SEAM'] as const

export type StandingSeamType = (typeof VANDERVALK_STANDING_SEAM_TYPE_OPTIONS)[number]

/** Vendor seam-clamp options returned for each ``seamType`` (suitability + BOM frame). */
export const SEAM_CLAMP_TYPES_BY_SEAM_TYPE: Record<StandingSeamType, readonly string[]> = {
  FLAT_SEAM: ['FLAT_CLAMP', 'STRAIGHT_CLAMP'],
  ROUND_SEAM: ['ROUND_CLAMP'],
}

export function getSeamClampTypesForSeamType(seamType: string | null | undefined): string[] {
  if (seamType == null || String(seamType).trim() === '') {
    return []
  }
  const clamps = SEAM_CLAMP_TYPES_BY_SEAM_TYPE[String(seamType) as StandingSeamType]
  return clamps ? [...clamps] : []
}

export type PitchedCladdingOptionKey =
  | 'steelThickness'
  | 'corrugatedType'
  | 'substructureMaterial'
  | 'standingSeamType'
  | 'purlinPitch'
  | 'purlinCorrectionOffsetY'
  | 'rafterPitch'
  | 'rafterCorrectionOffsetX'
  | 'claddingPitch'
  | 'claddingCorrectionOffsetX'
  | 'claddingThickness'
  | 'tileLength'

/** Pitched cladding fields sent on BOM only — omitted from suitability POST and cache keys. */
export const PITCHED_CLADDING_BOM_ONLY_OPTION_KEYS: readonly PitchedCladdingOptionKey[] = [
  'steelThickness',
]

export type PitchedCladdingOptions = Partial<Record<PitchedCladdingOptionKey, string | number>>

export type PitchedCladdingFieldKind = 'enum' | 'number'

export type PitchedCladdingFieldDefinition = {
  key: PitchedCladdingOptionKey
  kind: PitchedCladdingFieldKind
  /** Enum choices (string or numeric serialized as string in Select). */
  options?: readonly (string | number)[]
  /** Default when field is shown; offset fields use 0. */
  default?: string | number
  /** Show only when another field equals `value`. */
  conditionalOn?: { key: PitchedCladdingOptionKey; value: string }
  /** Optional numeric bounds (inclusive) for number fields. */
  min?: number
  max?: number
}

const OFFSET_DEFAULT = 0
const PITCHED_NUMBER_DEFAULT = 1
const CLADDING_CORRECTION_OFFSET_X_DEFAULT = 100
const CLADDING_PITCH_DEFAULT = 333
const TILED_CLADDING_PITCH_DEFAULT = 150
const TILED_RAFTER_PITCH_DEFAULT = 600
const TILED_TILE_LENGTH_DEFAULT = 300
const CORRUGATED_STEEL_CLADDING_PITCH_DEFAULT = 170
const CORRUGATED_STEEL_PURLIN_PITCH_DEFAULT = 1300
const CORRUGATED_STEEL_THICKNESS_DEFAULT = 0.5
const TRAPEZOIDAL_STEEL_THICKNESS_DEFAULT = 0.5

/** Matches Django ``VanDerValkPitchedCladdingOptionsSerializer`` integer ``max_value``. */
export const PITCHED_CLADDING_NUMBER_MAX = 1_000_000

function isCorrectionOffsetField(key: PitchedCladdingOptionKey): boolean {
  return key.includes('Offset') || key.includes('offset')
}

function pitchedNumberField(key: PitchedCladdingOptionKey): PitchedCladdingFieldDefinition {
  return { key, kind: 'number', default: PITCHED_NUMBER_DEFAULT }
}

function pitchedOffsetField(key: PitchedCladdingOptionKey): PitchedCladdingFieldDefinition {
  return { key, kind: 'number', default: OFFSET_DEFAULT }
}

const STEEL_THICKNESS_FIELD: PitchedCladdingFieldDefinition = {
  key: 'steelThickness',
  kind: 'enum',
  options: VANDERVALK_STEEL_THICKNESS_OPTIONS,
  default: TRAPEZOIDAL_STEEL_THICKNESS_DEFAULT,
}

const CLADDING_PITCH_FIELD: PitchedCladdingFieldDefinition = {
  key: 'claddingPitch',
  kind: 'number',
  default: CLADDING_PITCH_DEFAULT,
}

const CLADDING_CORRECTION_OFFSET_X_FIELD: PitchedCladdingFieldDefinition = {
  key: 'claddingCorrectionOffsetX',
  kind: 'number',
  default: CLADDING_CORRECTION_OFFSET_X_DEFAULT,
}

const CLADDING_THICKNESS_FIELD: PitchedCladdingFieldDefinition = {
  key: 'claddingThickness',
  kind: 'enum',
  options: VANDERVALK_STEEL_THICKNESS_OPTIONS,
  default: CORRUGATED_STEEL_THICKNESS_DEFAULT,
  conditionalOn: { key: 'corrugatedType', value: 'STEEL' },
}

/** Metal Tin centre distance: 75–1500 mm. */
const CORRUGATED_STEEL_CLADDING_PITCH_FIELD: PitchedCladdingFieldDefinition = {
  key: 'claddingPitch',
  kind: 'number',
  default: CORRUGATED_STEEL_CLADDING_PITCH_DEFAULT,
  min: 75,
  max: 1500,
  conditionalOn: { key: 'corrugatedType', value: 'STEEL' },
}

/** Fibre Cement cladding pitch (BOM-only): generic default, not Metal Tin centre-distance defaults. */
const CORRUGATED_FIBRE_CLADDING_PITCH_FIELD: PitchedCladdingFieldDefinition = {
  ...pitchedNumberField('claddingPitch'),
  conditionalOn: { key: 'corrugatedType', value: 'FIBER_CEMENT' },
}

/** Metal Tin purlin spacing: 0–1500 mm. */
const CORRUGATED_STEEL_PURLIN_PITCH_FIELD: PitchedCladdingFieldDefinition = {
  key: 'purlinPitch',
  kind: 'number',
  default: CORRUGATED_STEEL_PURLIN_PITCH_DEFAULT,
  min: 0,
  max: 1500,
  conditionalOn: { key: 'corrugatedType', value: 'STEEL' },
}

/** Fibre Cement purlin distance: 0–1500 mm, default 500. */
const CORRUGATED_FIBRE_PURLIN_PITCH_FIELD: PitchedCladdingFieldDefinition = {
  key: 'purlinPitch',
  kind: 'number',
  default: 500,
  min: 0,
  max: 1500,
  conditionalOn: { key: 'corrugatedType', value: 'FIBER_CEMENT' },
}

/** Metal Tin (corrugatedType STEEL): default substructure to steel. */
const CORRUGATED_STEEL_SUBSTRUCTURE_FIELD: PitchedCladdingFieldDefinition = {
  key: 'substructureMaterial',
  kind: 'enum',
  options: ['WOOD', 'STEEL'],
  default: 'STEEL',
  conditionalOn: { key: 'corrugatedType', value: 'STEEL' },
}

/** Corrugated Fibre Cement: keep wood substructure default. */
const CORRUGATED_FIBRE_SUBSTRUCTURE_FIELD: PitchedCladdingFieldDefinition = {
  key: 'substructureMaterial',
  kind: 'enum',
  options: ['WOOD', 'STEEL'],
  default: 'WOOD',
  conditionalOn: { key: 'corrugatedType', value: 'FIBER_CEMENT' },
}

const TILED_RAFTER_PITCH_FIELD: PitchedCladdingFieldDefinition = {
  key: 'rafterPitch',
  kind: 'number',
  default: TILED_RAFTER_PITCH_DEFAULT,
}

const TILED_RAFTER_CORRECTION_OFFSET_X_FIELD: PitchedCladdingFieldDefinition = {
  key: 'rafterCorrectionOffsetX',
  kind: 'number',
  default: CLADDING_CORRECTION_OFFSET_X_DEFAULT,
}

const TILED_CLADDING_PITCH_FIELD: PitchedCladdingFieldDefinition = {
  key: 'claddingPitch',
  kind: 'number',
  default: TILED_CLADDING_PITCH_DEFAULT,
}

const TILED_TILE_LENGTH_FIELD: PitchedCladdingFieldDefinition = {
  key: 'tileLength',
  kind: 'number',
  default: TILED_TILE_LENGTH_DEFAULT,
}

const FIELDS_BY_CLADDING_TYPE: Record<VanDerValkPitchedCladdingType, PitchedCladdingFieldDefinition[]> = {
  STEEL_DECK: [STEEL_THICKNESS_FIELD, CLADDING_PITCH_FIELD, CLADDING_CORRECTION_OFFSET_X_FIELD],
  CORRUGATED: [
    { key: 'corrugatedType', kind: 'enum', options: ['STEEL', 'FIBER_CEMENT'] },
    CORRUGATED_STEEL_SUBSTRUCTURE_FIELD,
    CORRUGATED_FIBRE_SUBSTRUCTURE_FIELD,
    CORRUGATED_STEEL_PURLIN_PITCH_FIELD,
    CORRUGATED_FIBRE_PURLIN_PITCH_FIELD,
    pitchedOffsetField('purlinCorrectionOffsetY'),
    // STEEL: centre distance + thickness. FIBER_CEMENT: generic claddingPitch (BOM-only / suitability omitted).
    CORRUGATED_STEEL_CLADDING_PITCH_FIELD,
    CORRUGATED_FIBRE_CLADDING_PITCH_FIELD,
    pitchedOffsetField('claddingCorrectionOffsetX'),
    CLADDING_THICKNESS_FIELD,
  ],
  STANDING_SEAM: [
    { key: 'standingSeamType', kind: 'enum', options: VANDERVALK_STANDING_SEAM_TYPE_OPTIONS },
    CLADDING_PITCH_FIELD,
    CLADDING_CORRECTION_OFFSET_X_FIELD,
  ],
  TILED: [
    TILED_RAFTER_PITCH_FIELD,
    TILED_RAFTER_CORRECTION_OFFSET_X_FIELD,
    TILED_CLADDING_PITCH_FIELD,
    CLADDING_CORRECTION_OFFSET_X_FIELD,
    TILED_TILE_LENGTH_FIELD,
  ],
  SLATE: [
    TILED_RAFTER_PITCH_FIELD,
    TILED_CLADDING_PITCH_FIELD,
    CLADDING_CORRECTION_OFFSET_X_FIELD,
  ],
  PLAIN: [pitchedNumberField('rafterPitch'), CLADDING_PITCH_FIELD, pitchedOffsetField('claddingCorrectionOffsetX')],
  MEMBRANE: [pitchedNumberField('purlinPitch'), CLADDING_PITCH_FIELD, pitchedOffsetField('claddingCorrectionOffsetX')],
}

export function isVanDerValkPitchedCladdingType(value: unknown): value is VanDerValkPitchedCladdingType {
  return typeof value === 'string' && (VANDERVALK_PITCHED_CLADDING_TYPES as readonly string[]).includes(value)
}

function fieldIsVisible(field: PitchedCladdingFieldDefinition, options: PitchedCladdingOptions): boolean {
  if (!field.conditionalOn) {
    return true
  }
  const current = options[field.conditionalOn.key]
  return current != null && String(current) === field.conditionalOn.value
}

/** Field definitions for a cladding type, respecting conditional visibility. */
export function getPitchedCladdingFieldsForType(
  claddingType: VanDerValkPitchedCladdingType,
  options: PitchedCladdingOptions = {},
  opts?: { excludeKeys?: PitchedCladdingOptionKey[] }
): PitchedCladdingFieldDefinition[] {
  const exclude = new Set(opts?.excludeKeys ?? [])
  const defs = FIELDS_BY_CLADDING_TYPE[claddingType] ?? []
  return defs.filter((field) => fieldIsVisible(field, options) && !exclude.has(field.key))
}

export function getStandingSeamTypeFromPitchedCladdingOptions(
  options: PitchedCladdingOptions | null | undefined
): StandingSeamType | null {
  const raw = options?.standingSeamType ?? (options as { seamType?: unknown } | undefined)?.seamType
  if (raw == null || String(raw).trim() === '') {
    return null
  }
  const value = String(raw)
  return (VANDERVALK_STANDING_SEAM_TYPE_OPTIONS as readonly string[]).includes(value)
    ? (value as StandingSeamType)
    : null
}

export type VanDerValkCorrugatedTypeOption = 'STEEL' | 'FIBER_CEMENT'

const VANDERVALK_CORRUGATED_TYPE_OPTIONS = ['STEEL', 'FIBER_CEMENT'] as const

/** Roof-synced ``corrugatedType`` from pitched cladding options. */
export function getCorrugatedTypeFromPitchedCladdingOptions(
  options: PitchedCladdingOptions | null | undefined
): VanDerValkCorrugatedTypeOption | null {
  const raw = options?.corrugatedType
  if (raw == null || String(raw).trim() === '') {
    return null
  }
  const value = String(raw)
  return (VANDERVALK_CORRUGATED_TYPE_OPTIONS as readonly string[]).includes(value)
    ? (value as VanDerValkCorrugatedTypeOption)
    : null
}

/** Seam type for standing-seam BOM enrichment (cladding options, then last suitability fetch). */
export function resolveStandingSeamTypeForBom(
  options: PitchedCladdingOptions | null | undefined,
  suitabilitySeamType: string | null | undefined
): StandingSeamType | null {
  return (
    getStandingSeamTypeFromPitchedCladdingOptions(options) ??
    getStandingSeamTypeFromPitchedCladdingOptions(
      { seamType: suitabilitySeamType } as PitchedCladdingOptions
    )
  )
}

/** All field definitions for a cladding type (including conditionally hidden). */
export function getAllPitchedCladdingFieldDefinitions(
  claddingType: VanDerValkPitchedCladdingType
): PitchedCladdingFieldDefinition[] {
  return FIELDS_BY_CLADDING_TYPE[claddingType] ?? []
}

/**
 * Default option values: pitch/length numbers (1), correction offsets (0), steel thickness (first enum).
 * Pass ``options`` (e.g. ``corrugatedType``) so conditional Metal Tin fields are not applied to Fibre Cement.
 */
export function getDefaultPitchedCladdingOptions(
  claddingType: VanDerValkPitchedCladdingType,
  options: PitchedCladdingOptions = {}
): PitchedCladdingOptions {
  const out: PitchedCladdingOptions = {}
  for (const field of getAllPitchedCladdingFieldDefinitions(claddingType)) {
    if (field.default === undefined) {
      continue
    }
    if (!fieldIsVisible(field, options)) {
      continue
    }
    out[field.key] = field.default
  }
  return out
}

function isValidPositiveNumber(value: unknown, max = PITCHED_CLADDING_NUMBER_MAX): value is number {
  if (value === '' || value == null) {
    return false
  }
  const n = typeof value === 'number' ? value : Number(String(value).trim())
  return Number.isFinite(n) && n >= 0 && n <= max
}

function isValidRequiredPositiveNumber(value: unknown, max = PITCHED_CLADDING_NUMBER_MAX): boolean {
  if (value === '' || value == null) {
    return false
  }
  const n = typeof value === 'number' ? value : Number(String(value).trim())
  return Number.isFinite(n) && n > 0 && n <= max
}

function isValidNumberInFieldBounds(value: unknown, field: PitchedCladdingFieldDefinition): boolean {
  if (value === '' || value == null) {
    return false
  }
  const n = typeof value === 'number' ? value : Number(String(value).trim())
  if (!Number.isFinite(n)) {
    return false
  }
  const min = field.min ?? (isCorrectionOffsetField(field.key) ? 0 : 1)
  const max = field.max ?? PITCHED_CLADDING_NUMBER_MAX
  return n >= min && n <= max
}

function fieldValueIsComplete(field: PitchedCladdingFieldDefinition, options: PitchedCladdingOptions): boolean {
  const raw = options[field.key]
  if (field.kind === 'enum') {
    if (raw == null || String(raw).trim() === '') {
      return false
    }
    if (field.options?.length) {
      return field.options.some((opt) => String(opt) === String(raw))
    }
    return true
  }
  if (field.min != null || field.max != null) {
    const value = raw === undefined || raw === '' ? field.default : raw
    return isValidNumberInFieldBounds(value, field)
  }
  if (field.default !== undefined && (raw === undefined || raw === '')) {
    if (isCorrectionOffsetField(field.key)) {
      return isValidPositiveNumber(field.default)
    }
    if (field.kind === 'number' && typeof field.default === 'number' && field.default > 0) {
      return isValidRequiredPositiveNumber(field.default)
    }
  }
  if (isCorrectionOffsetField(field.key)) {
    return isValidPositiveNumber(raw ?? field.default ?? OFFSET_DEFAULT)
  }
  return isValidRequiredPositiveNumber(raw ?? field.default)
}

/** True when every visible required field for the cladding type has a valid value. */
export function isPitchedCladdingOptionsComplete(
  claddingType: VanDerValkPitchedCladdingType,
  options: PitchedCladdingOptions | null | undefined
): boolean {
  if (!options) {
    return false
  }
  const visibleFields = getPitchedCladdingFieldsForType(claddingType, options)
  return visibleFields.every((field) => fieldValueIsComplete(field, options))
}

function coerceFieldValueForVendor(
  field: PitchedCladdingFieldDefinition,
  raw: string | number | undefined
): string | number | undefined {
  if (raw == null || raw === '') {
    if (field.default !== undefined) {
      return field.default
    }
    return undefined
  }
  if (field.kind === 'enum' && field.key === 'steelThickness') {
    return Number(raw)
  }
  if (field.kind === 'enum' && field.key === 'claddingThickness') {
    return Number(raw)
  }
  if (field.kind === 'number') {
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim())
    return Number.isFinite(n) ? n : undefined
  }
  return String(raw)
}

/** Stable cache key for suitability invalidation (complete options only). */
export function buildPitchedCladdingOptionsCacheKey(
  claddingType: VanDerValkPitchedCladdingType,
  options: PitchedCladdingOptions | null | undefined
): string | null {
  if (!isPitchedCladdingOptionsComplete(claddingType, options)) {
    return null
  }
  const suitabilityOpts = buildSuitabilityCladdingOptions(claddingType, options!)
  return JSON.stringify(suitabilityOpts ?? { claddingType })
}

/** Cladding options for pitched suitability POST (excludes BOM-only fields such as ``steelThickness``). */
export function buildSuitabilityCladdingOptions(
  claddingType: VanDerValkPitchedCladdingType,
  options: PitchedCladdingOptions
): Record<string, string | number> | undefined {
  if (claddingType === 'STEEL_DECK') {
    return undefined
  }
  const bomOnly = new Set(PITCHED_CLADDING_BOM_ONLY_OPTION_KEYS)
  const full = buildVendorCladdingOptions(claddingType, options)
  const result: Record<string, string | number> = { claddingType: full.claddingType as string }
  for (const [key, value] of Object.entries(full)) {
    if (key === 'claddingType' || bomOnly.has(key as PitchedCladdingOptionKey)) {
      continue
    }
    result[key] = value as string | number
  }
  // CORRUGATED FIBER_CEMENT: claddingPitch is BOM-only (still shown in UI / vendor BOM payload).
  if (options.corrugatedType === 'FIBER_CEMENT') {
    delete result.claddingPitch
  }
  if (Object.keys(result).length <= 1) {
    return undefined
  }
  return result
}

/** Vendor ``roofSlope.claddingOptions`` object for pitched BOM POST. */
export function buildVendorCladdingOptions(
  claddingType: VanDerValkPitchedCladdingType,
  options: PitchedCladdingOptions
): Record<string, string | number> {
  const result: Record<string, string | number> = { claddingType }
  for (const field of getPitchedCladdingFieldsForType(claddingType, options)) {
    const coerced = coerceFieldValueForVendor(field, options[field.key])
    if (coerced !== undefined) {
      result[field.key] = coerced
    }
  }
  if (claddingType === 'STANDING_SEAM' && result.standingSeamType != null) {
    result.seamType = result.standingSeamType
  }
  return result
}

/** i18n keys for pitched cladding field labels. */
export const PITCHED_CLADDING_FIELD_LABEL_KEY: Record<PitchedCladdingOptionKey, string> = {
  steelThickness: 'mount_vdv_steel_thickness',
  corrugatedType: 'mount_vdv_corrugated_type',
  substructureMaterial: 'mount_vdv_substructure_material',
  standingSeamType: 'mount_vdv_standing_seam_type',
  purlinPitch: 'mount_vdv_purlin_pitch',
  purlinCorrectionOffsetY: 'mount_vdv_purlin_correction_offset_y',
  rafterPitch: 'mount_vdv_rafter_pitch',
  rafterCorrectionOffsetX: 'mount_vdv_rafter_correction_offset_x',
  claddingPitch: 'mount_vdv_cladding_pitch',
  claddingCorrectionOffsetX: 'mount_vdv_cladding_correction_offset_x',
  claddingThickness: 'mount_vdv_cladding_thickness',
  tileLength: 'mount_vdv_tile_length',
}

/** i18n keys for enum option labels (field + value). */
export function pitchedCladdingEnumOptionLabelKey(field: PitchedCladdingOptionKey, value: string | number): string {
  if (field === 'corrugatedType') {
    return value === 'STEEL' ? 'mount_vdv_corrugated_type_steel' : 'mount_vdv_corrugated_type_fiber_cement'
  }
  if (field === 'substructureMaterial') {
    return value === 'WOOD' ? 'mount_vdv_substructure_material_wood' : 'mount_vdv_substructure_material_steel'
  }
  if (field === 'standingSeamType') {
    return value === 'FLAT_SEAM' ? 'mount_vdv_standing_seam_type_flat' : 'mount_vdv_standing_seam_type_round'
  }
  return String(value)
}

/** Roof-type-specific i18n keys overriding generic ``PITCHED_CLADDING_FIELD_LABEL_KEY`` entries. */
const PITCHED_CLADDING_FIELD_LABEL_KEY_BY_TYPE: Partial<
  Record<VanDerValkPitchedCladdingType, Partial<Record<PitchedCladdingOptionKey, string>>>
> = {
  STANDING_SEAM: { claddingPitch: 'mount_vdv_ctc_of_seams' },
  STEEL_DECK: { claddingPitch: 'mount_vdv_seam_distance' },
  TILED: { rafterPitch: 'mount_vdv_rafter_spacing' },
  SLATE: { rafterPitch: 'mount_vdv_rafter_spacing' },
  CORRUGATED: { purlinPitch: 'mount_vdv_purlin_spacing' },
}

export function getPitchedCladdingFieldLabelKey(
  claddingType: VanDerValkPitchedCladdingType,
  fieldKey: PitchedCladdingOptionKey
): string {
  return (
    PITCHED_CLADDING_FIELD_LABEL_KEY_BY_TYPE[claddingType]?.[fieldKey] ??
    PITCHED_CLADDING_FIELD_LABEL_KEY[fieldKey]
  )
}
